import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { loadAdSenseOnce } from "@/lib/adsense";

export const CONSENT_KEY = "nox_cookie_consent";

export function getCookieConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

export function setCookieConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    // Dispatch event so AdUnit components can react immediately
    window.dispatchEvent(new CustomEvent("nox-consent-change", { detail: value }));
  } catch {}
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Show banner if user hasn't made a choice yet
    const existing = getCookieConsent();
    if (!existing) {
      // Small delay so it doesn't pop immediately on page load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    // If previously accepted, load AdSense immediately
    if (existing === "accepted") loadAdSenseOnce();
  }, []);

  const accept = () => {
    setCookieConsent("accepted");
    setVisible(false);
    loadAdSenseOnce();
  };

  const decline = () => {
    setCookieConsent("declined");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-[480px] z-[200]"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
        >
          <div className="rounded-2xl border border-white/15 bg-[#0F1015]/98 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                  <Cookie className="h-4 w-4 text-primary" />
                </div>
                <span className="font-heading font-black text-base tracking-tight text-white">We use cookies</span>
              </div>
              <button
                onClick={decline}
                className="text-white/40 hover:text-white/80 transition-colors shrink-0 mt-0.5"
                aria-label="Close without accepting"
                data-testid="cookie-consent-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <p className="text-sm text-muted-foreground leading-relaxed mb-1">
              We use essential cookies to operate the site and, with your consent, advertising cookies
              (Google AdSense) to serve relevant ads. No personal data is sold.
            </p>

            {/* Details toggle */}
            <button
              onClick={() => setShowDetails((s) => !s)}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-[#00C2D9] transition-colors mb-3 mt-1"
              data-testid="cookie-consent-details-toggle"
            >
              <Settings className="h-3 w-3" />
              {showDetails ? "Hide details" : "What do we use cookies for?"}
            </button>

            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="rounded-xl border border-white/10 bg-[#050508]/60 p-3 mb-3 text-xs text-muted-foreground space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="h-2 w-2 rounded-full bg-primary mt-1 shrink-0" />
                      <div>
                        <span className="text-white font-semibold">Essential</span> — Authentication sessions &amp; core functionality. Always active.
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="h-2 w-2 rounded-full bg-yellow-400 mt-1 shrink-0" />
                      <div>
                        <span className="text-white font-semibold">Advertising</span> — Google AdSense &amp; DoubleClick (third-party). Only with your consent.{" "}
                        <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Manage Google ads preferences</a>.
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex items-center gap-2.5">
              <Button
                onClick={accept}
                className="flex-1 bg-primary text-black hover:bg-[#00C2D9] font-semibold h-9 text-sm"
                data-testid="cookie-consent-accept"
              >
                Accept all
              </Button>
              <Button
                onClick={decline}
                variant="outline"
                className="flex-1 border-white/15 h-9 text-sm hover:border-white/30 text-white/80"
                data-testid="cookie-consent-decline"
              >
                Decline
              </Button>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground/60 text-center">
              By using our site you agree to our{" "}
              <Link to="/privacy-policy" className="text-primary/80 hover:text-primary hover:underline" onClick={accept}>
                Privacy Policy
              </Link>{" "}
              &amp;{" "}
              <Link to="/terms" className="text-primary/80 hover:text-primary hover:underline" onClick={accept}>
                Terms of Service
              </Link>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
